import { useCallback, useMemo } from 'react';

import type { DxfMappingRole } from '@frontend/api';

import { roleSelectionState } from 'modules/common/viewer/dxfViewer/session/mappingModel';
import type { PanelSelectionMode } from 'modules/common/viewer/dxfViewer/session/selectionModifiers';
import type {
  DxfViewerSession,
  ViewerMode,
} from 'modules/common/viewer/dxfViewer/session/types';
import { useReadableStore } from 'modules/common/viewer/dxfViewer/session/useSessionState';

export type DxfMappingPanelState = {
  /** Roles present on this file, with how many entities carry each. */
  counts: ReadonlyMap<DxfMappingRole, number>;
  /**
   * Roles holding at least one entity that can actually be picked, and so worth
   * offering as a click target.
   */
  selectableRoles: ReadonlySet<DxfMappingRole>;
  /**
   * Roles whose every selectable entity is selected.
   *
   * All or nothing, like `selectedLayers`: a partially-caught role is absent, so
   * `has` answers "is this role selected" outright.
   */
  selectedRoles: ReadonlySet<DxfMappingRole>;
  /** False when the file carries no mapping at all — the empty state. */
  hasMapping: boolean;
  mode: ViewerMode;
  isMappingColoringOn: boolean;
  onSelectRole: (role: DxfMappingRole, mode: PanelSelectionMode) => void;
  onHoverRole: (role: DxfMappingRole | null) => void;
  onToggleMappingColoring: () => void;
};

/**
 * Connects the mapping panel to the session. Call only under a ready drawing.
 *
 * Subscribes to the mapping slice rather than to `counts` / `roleIndex`
 * individually: those are methods on the manager (see `createMappingController`)
 * derived only from this slice, so the snapshot is both the notification and
 * the `hasMapping` read. Entering mapping mode is the `dxfMapping.edit` action.
 */
export const useDxfMappingPanel = (
  session: DxfViewerSession,
): DxfMappingPanelState => {
  const mapping = useReadableStore(session.mapping);
  const mode = useReadableStore(session.mode);
  const isMappingColoringOn = useReadableStore(
    session.overlays,
    (current) => current.mappingColoring,
  );
  const selection = useReadableStore(session.selection);
  const model = useReadableStore(session.model);
  const hiddenLayers = useReadableStore(session.hiddenLayers);

  // `roleIndex` is memoised against the assignments it was built from, so its
  // identity is a sound dependency: it only changes when the mapping does.
  const roleIndex = session.mapping.roleIndex();
  const { selectableRoles, selectedRoles } = useMemo(
    () =>
      model
        ? roleSelectionState(selection, roleIndex, model, hiddenLayers)
        : {
            selectableRoles: new Set<DxfMappingRole>(),
            selectedRoles: new Set<DxfMappingRole>(),
          },
    [selection, roleIndex, model, hiddenLayers],
  );

  const onSelectRole = useCallback(
    (role: DxfMappingRole, mode: PanelSelectionMode) =>
      session.mapping.selectRole(role, mode),
    [session],
  );

  const onHoverRole = useCallback(
    (role: DxfMappingRole | null) => session.mapping.setHoveredRole(role),
    [session],
  );

  const onToggleMappingColoring = useCallback(
    () => session.overlays.toggle('mappingColoring'),
    [session],
  );

  return {
    counts: session.mapping.counts(),
    selectableRoles,
    selectedRoles,
    // Read off the effective mapping rather than off `counts`: a mapping every one
    // of whose entities is unstitched still counts as a mapping, and the panel
    // should not offer to create one that already exists.
    hasMapping: mapping.draft !== null || mapping.saved !== null,
    mode,
    isMappingColoringOn,
    onSelectRole,
    onHoverRole,
    onToggleMappingColoring,
  };
};
